import { type Category } from '@gozoki/api-types';
import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { StorePricingGridRow } from './StorePricingGridRow';

interface StorePricingGridProps {
    storeId: number;
    products: { categories: Category[]; label: string; reference: string }[];
    storeProductPricesMap: Record<
        string,
        {
            sellPrice?: number;
            franco?: number;
            coldStartStockMin?: number;
            agPromo?: number;
        }
    >;
    refetch: () => void;
}

const columns = [
    'Produit',
    'Prix de vente (€)',
    'Franco',
    'Stock min cold start',
    'Réduction anti-gaspi (%)',
    'Actions',
];

export const StorePricingGrid = (props: StorePricingGridProps) => {
    const { products, storeId, storeProductPricesMap, refetch } = props;
    const [editCell, setEditCell] = useState<string | null>(null);

    const handleModify = useCallback((reference: string) => {
        setEditCell(reference);
    }, []);

    const handleSave = useCallback(() => {
        if (!editCell) return;
        refetch();
        setEditCell(null);
    }, [editCell, refetch]);

    const handleCancel = useCallback(() => {
        setEditCell(null);
    }, []);

    const rows = useMemo(() => {
        return products.map(({ label, reference }) => {
            const pricingValues = storeProductPricesMap[reference] || {
                sellPrice: undefined,
                franco: undefined,
                coldStartStockMin: undefined,
                agPromo: undefined,
            };

            return { pricingValues, label, reference };
        });
    }, [products, storeProductPricesMap]);

    return (
        <Stack gap={1} p={1}>
            <Stack flexDirection="row" gap={1} borderBottom="1px solid lightGray" pb={2}>
                {columns.map((column, index) => {
                    const flex = column === 'Produit' ? 2 : 1;
                    return (
                        <Typography
                            key={`column-${column}-${index}`}
                            flex={flex}
                            px={column === 'Produit' ? 1 : 0}
                        >
                            {column}
                        </Typography>
                    );
                })}
            </Stack>
            {rows.map(({ pricingValues, label, reference }) => (
                <StorePricingGridRow
                    key={`row-${reference}`}
                    storeId={storeId}
                    productReference={reference}
                    pricingValues={pricingValues}
                    label={label}
                    edit={editCell === reference}
                    disabled={editCell !== null && editCell !== reference}
                    onModify={handleModify}
                    onSave={handleSave}
                    onCancel={handleCancel}
                />
            ))}
        </Stack>
    );
};
